import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useSubmitCatalogueForm } from "../hooks/useSubmitCatalogueForm";
import Icon from "../../../../../common/components/Icon/Icon";
import {
  createCatalogueFormSchema,
  CatalogueFormData,
} from "../helpers/catalogueValidation";

const CatalogueForm: React.FC = () => {
  const { t } = useTranslation();
  const { submitCatalogueForm, isLoading, isSuccess, isError } =
    useSubmitCatalogueForm();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CatalogueFormData>({
    resolver: zodResolver(createCatalogueFormSchema(t)),
  });

  useEffect(() => {
    register("formLoadTimestamp");
    setValue("formLoadTimestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);

  if (isSuccess) {
    return (
      <div className="cfp-success-state">
        <div className="cfp-success-state__icon">
          <Icon iconName="check-circle" className="icon--filled" />
        </div>
        <h3 className="cfp-success-state__title">
          {t("catalogueForm.successTitle", "Thank you!")}
        </h3>
        <p className="cfp-success-state__message">
          {t(
            "catalogueForm.successMessage",
            "Our catalogue will be sent to your email address.",
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      className={`cfp-form ${isLoading ? "is-loading" : ""}`}
      onSubmit={handleSubmit((data) => submitCatalogueForm(data))}
      noValidate
    >
      <div className="cfp-form__header">
        <h4 className="cfp-form__sub-title">
          {t("catalogueForm.subTitle", "Catalogue Request")}
        </h4>
        <h2 className="cfp-form__title">
          {t("catalogueForm.title", "Download Our Digital Catalogue")}
        </h2>
      </div>

      <fieldset className="cfp-form__fieldset" disabled={isLoading}>
        {/* Honeypot */}
        <div className="cfp-form__honeypot" aria-hidden="true">
          <input {...register("nickname")} tabIndex={-1} autoComplete="off" />
        </div>

        <div className="cfp-form__grid">
          <div className="cfp-field">
            <label className="cfp-field__label">
              {t("catalogueForm.name", "Name")} *
            </label>
            <input
              {...register("name")}
              className={`cfp-field__input ${errors.name ? "has-error" : ""}`}
            />
            {errors.name && (
              <span className="cfp-field__error">{errors.name.message}</span>
            )}
          </div>

          <div className="cfp-field">
            <label className="cfp-field__label">
              {t("catalogueForm.surname", "Surname")} *
            </label>
            <input
              {...register("surname")}
              className={`cfp-field__input ${errors.surname ? "has-error" : ""}`}
            />
            {errors.surname && (
              <span className="cfp-field__error">{errors.surname.message}</span>
            )}
          </div>
        </div>

        <div className="cfp-field">
          <label className="cfp-field__label">
            {t("catalogueForm.email", "Email")} *
          </label>
          <input
            type="email"
            {...register("email")}
            className={`cfp-field__input ${errors.email ? "has-error" : ""}`}
          />
          {errors.email && (
            <span className="cfp-field__error">{errors.email.message}</span>
          )}
        </div>

        <div className="cfp-field">
          <label className="cfp-field__label">
            {t("catalogueForm.phone", "Phone")} *
          </label>
          <input
            type="tel"
            {...register("phone")}
            className={`cfp-field__input ${errors.phone ? "has-error" : ""}`}
          />
          {errors.phone && (
            <span className="cfp-field__error">{errors.phone.message}</span>
          )}
        </div>

        <div className="cfp-field">
          <label className="cfp-field__label">
            {t("catalogueForm.company", "Company Name (Optional)")}
          </label>
          <input {...register("company")} className="cfp-field__input" />
        </div>

        <div className="cfp-field">
          <label className="cfp-field__label">
            {t("catalogueForm.occupation", "Occupation (Optional)")}
          </label>
          <input {...register("occupation")} className="cfp-field__input" />
        </div>

        {isError && (
          <div className="cfp-form__alert cfp-form__alert--error">
            <Icon iconName="exclamation-circle" className="icon--filled" />
            <span>
              {t(
                "catalogueForm.errorGeneric",
                "Please check your information.",
              )}
            </span>
          </div>
        )}

        <button type="submit" className="cfp-form__submit">
          {isLoading
            ? t("catalogueForm.sending", "Sending...")
            : t("catalogueForm.submit", "Download Catalogue")}
        </button>
      </fieldset>
    </form>
  );
};

export default CatalogueForm;
